import { Col, Text } from 'components';
import { formatLocale, theme } from 'lib';
import { memo, useMemo } from 'react';
import { TableComponents } from 'screens/Admin/components';
import styled from 'styled-components';

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  color: #e53935;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #ffebee;
  }
`;

const Thumbnail = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  background: ${theme.GREY_10};
  flex-shrink: 0;
`;

// Stands in for a missing thumbnail at exactly the image's size, so a row without one keeps
// the same height and the column keeps the same width.
const ThumbnailPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: ${theme.GREY_10};
  flex-shrink: 0;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.live ? '#FFF3E0' : '#E8F5E9')};
  color: ${(p) => (p.live ? '#E65100' : '#2E7D32')};
`;

const ListItem = memo(({ item, isSelected, onClick, onRequestDelete }) => {
  const isLive = item.type === 'live';

  // The list grows without bound and rows re-render whenever the screen above them does, so
  // the date is formatted once per row rather than once per render — formatLocale builds a
  // Date and reads the store for the active locale on every call.
  const postedAt = useMemo(
    () => (item.createdAt ? formatLocale(new Date(item.createdAt), 'LLL do yyy, hh:mm a') : '-'),
    [item.createdAt]
  );

  return (
    <TableComponents.Tr onClick={() => onClick(item._id)} isSelected={isSelected}>
      <TableComponents.Td isSelected={isSelected} first>
        <ThumbnailWrapper>
          {item.media?.thumbnail ? (
            // decoding="async" keeps the decode off the main thread so it cannot drop a frame
            // mid-scroll. Deliberately NOT lazy: the thumbnails are already resized server
            // side, and loading them on demand made scrolling back up pay for every image the
            // downward scroll had skipped.
            //
            // Low priority because a page is 50 rows: eagerly fetching 50 thumbnails at once
            // otherwise competes with the list and summary requests for connections, which is
            // what made the very first scroll after a reload feel stuck.
            <Thumbnail
              src={item.media.thumbnail}
              width={48}
              height={48}
              decoding="async"
              fetchpriority="low"
              alt="thumb"
            />
          ) : (
            <ThumbnailPlaceholder />
          )}
        </ThumbnailWrapper>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Text>{item.owner?.name || '-'}</Text>
        {item.owner?.username && (
          <Text c1 col={theme.GREY_60}>
            @{item.owner.username}
          </Text>
        )}
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Text>{item.team?.name || '-'}</Text>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <TypeBadge live={isLive}>{isLive ? 'Live' : 'Challenge'}</TypeBadge>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Text c1 col={theme.GREY_70} maxWid="180px">
          {item.caption || '-'}
        </Text>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Col noFlex>
          <Text c1>{item.viewCount ?? 0}</Text>
        </Col>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Col noFlex>
          <Text c1>{item.reactionCount ?? 0}</Text>
        </Col>
      </TableComponents.Td>

      <TableComponents.Td isSelected={isSelected}>
        <Text c1>{postedAt}</Text>
      </TableComponents.Td>

      <TableComponents.Td wid="100px" isSelected={isSelected} onClick={(e) => e.stopPropagation()}>
        <DeleteButton onClick={() => onRequestDelete(item)}>Delete</DeleteButton>
      </TableComponents.Td>
    </TableComponents.Tr>
  );
});

export default ListItem;
